import React from 'react';
import { Link } from 'react-router-dom';

/**
 * About Us Card Component
 * Displays vision and mission in an elegant card design
 */
const AboutUsCard = ({ config, theme, isMobile }) => {
  // Get vision and mission from config or use defaults
  const vision = config?.vision || "Building a vibrant, spiritually connected community rooted in the teachings of Ahlulbayt (AS), where faith and knowledge flourish together";
  
  const mission = config?.mission || "Providing spiritual guidance and religious education to foster unity and brotherhood, supporting families through life's journey while preserving Islamic values and traditions";

  // Truncate text to show snippet with more content
  const truncateText = (text, maxLength = 200) => {
    if (typeof text !== 'string') return '';
    if (text.length <= maxLength) return text + '...';
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <div style={{
      marginTop: '64px',
      marginBottom: '64px'
    }}>
      {/* Section Header - Matching other sections */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <h2 style={{
          fontSize: isMobile ? '20px' : '24px',
          fontWeight: '600',
          color: theme?.colors?.headingColor || theme?.colors?.primary || '#0f172a',
          margin: 0
        }}>
          About Us
        </h2>
      </div>

      {/* Card Container with enhanced design */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: isMobile ? '28px' : '40px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        border: '1px solid #e5e7eb',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 14px 30px rgba(0, 0, 0, 0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.05)';
      }}>
        
        {/* Subtle background pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '100%',
          background: `linear-gradient(135deg, transparent 0%, ${theme?.colors?.primary}05 100%)`,
          pointerEvents: 'none'
        }} />

        {/* Large decorative quote for background */}
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          fontSize: '120px',
          color: theme?.colors?.primary || '#1e4273',
          opacity: 0.05,
          fontFamily: 'serif',
          lineHeight: 0.8,
          pointerEvents: 'none',
          userSelect: 'none'
        }}>
          "
        </div>
        
        {/* Vision Section */}
        <div style={{
          display: 'flex',
          marginBottom: '36px',
          position: 'relative'
        }}>
          {/* Gradient accent bar */}
          <div style={{
            width: '4px',
            background: `linear-gradient(180deg, ${theme?.colors?.primary || '#1e4273'} 0%, ${theme?.colors?.secondary || '#3b82f6'} 100%)`,
            borderRadius: '2px',
            marginRight: '24px',
            flexShrink: 0
          }} />
          
          {/* Vision Content */}
          <div style={{ flex: 1 }}>
            <h3 style={{
              color: theme?.colors?.primary || '#1e4273',
              fontSize: isMobile ? '1.2rem' : '1.4rem',
              marginBottom: '16px',
              fontWeight: '700',
              letterSpacing: '-0.02em',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                fontSize: '28px',
                opacity: 0.3,
                fontFamily: 'serif'
              }}>"</span>
              Our Vision
            </h3>
            <p style={{
              color: '#4b5563',
              fontSize: isMobile ? '0.95rem' : '1rem',
              lineHeight: '1.8',
              margin: 0,
              fontStyle: 'italic',
              paddingLeft: '16px',
              borderLeft: '2px solid transparent',
              transition: 'border-color 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderLeftColor = theme?.colors?.primary + '30' || '#1e427330';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderLeftColor = 'transparent';
            }}>
              {truncateText(vision)}
            </p>
          </div>
        </div>

        {/* Mission Section */}
        <div style={{
          display: 'flex',
          marginBottom: '52px',
          position: 'relative'
        }}>
          {/* Gradient accent bar */}
          <div style={{
            width: '4px',
            background: `linear-gradient(180deg, ${theme?.colors?.secondary || '#3b82f6'} 0%, ${theme?.colors?.accent || '#10b981'} 100%)`,
            borderRadius: '2px',
            marginRight: '24px',
            flexShrink: 0
          }} />
          
          {/* Mission Content */}
          <div style={{ flex: 1 }}>
            <h3 style={{
              color: theme?.colors?.primary || '#1e4273',
              fontSize: isMobile ? '1.2rem' : '1.4rem',
              marginBottom: '16px',
              fontWeight: '700',
              letterSpacing: '-0.02em',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                fontSize: '28px',
                opacity: 0.3,
                fontFamily: 'serif'
              }}>"</span>
              Our Mission
            </h3>
            <p style={{
              color: '#4b5563',
              fontSize: isMobile ? '0.95rem' : '1rem',
              lineHeight: '1.8',
              margin: 0,
              fontStyle: 'italic',
              paddingLeft: '16px',
              borderLeft: '2px solid transparent',
              transition: 'border-color 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderLeftColor = theme?.colors?.secondary + '30' || '#3b82f630';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderLeftColor = 'transparent';
            }}>
              {truncateText(mission)}
            </p>
          </div>
        </div>

        {/* Decorative divider */}
        <div style={{
          width: '60px',
          height: '2px',
          background: `linear-gradient(90deg, ${theme?.colors?.primary || '#1e4273'}, ${theme?.colors?.secondary || '#3b82f6'})`,
          margin: '32px auto 32px',
          borderRadius: '1px'
        }} />

        {/* Learn More Button */}
        <div style={{ textAlign: 'center', position: 'relative' }}>
          <Link
            to="/about"
            style={{
              display: 'inline-block',
              padding: '14px 32px',
              background: `linear-gradient(135deg, ${theme?.colors?.primary || '#1e4273'} 0%, ${theme?.colors?.primary || '#1e4273'}dd 100%)`,
              color: 'white',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.3s',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
            }}
          >
            Learn More About Us →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AboutUsCard;